import { BaseService } from "./base.service";
import { IRequisitionStatus } from "models/RequisitionStatusModel";
import { IRequisitionGridDetail } from "models/Requisition/RequisitionGridDetail";
import { TableFilterModel } from "models/TableFilterModel";
import { IRequisitionNumber } from "models/RequisitionNumberModel";
import RequisitionDetailModel, {
	IRequisitionContractDetail,
	IRequisitionPanel,
} from "models/Requisition/RequisitionDetailModel";
import { ITableNumberColumnFilter } from "models/TableNumberColumnFilter";
import { IRequisitionStepHistory } from "models/Requisition/RequisitionStepHistory";
import ReadyStatusRequisitionDetail from "models/Requisition/ReadyStatusRequisitionDetail";
import { IPreviousRequisitionStepHistory } from "models/Requisition/PreviousRequisitionStepHistory";
import { IDelegateToUser } from "models/Requisition/DelegateToUserSelection";
import { ICompletedStepHistory } from "models/Requisition/CompletedStepHistory";
import RequisitionConversionToBidDetail from "models/Requisition/RequisitionConversionToBidDetail";
import { IRequisitionSystemTemplate } from "models/SystemTemplateDetail";
import { AddUpdateRequisitionSystemTemplateSection } from "models/Requisition/AddUpdateRequisitionSystemTemplateSection";
import { RequisitionSystemTemplateModel } from "models/Requisition/RequisitionSystemTemplateDetail";
import { ProcurementMethodSelectionItem } from "models/Requisition/RequisitionProcurementMethodSelection";
import { ContractType } from "utility/enums/requisition-entry";
import { IPanelSupplierUser, ISupplierUser } from "models/SupplierUserModel";
import { IModulePhaseTransactionType } from "./../models/ModulePhaseTransactionTypeModel";
import { IItem } from "models/ItemModel";

class RequisitionServiceInternal extends BaseService {
	getRequisitionStatusList = async (): Promise<IRequisitionStatus[]> =>
		this.callApi<IRequisitionStatus[]>(
			`RequisitionStatus/GetRequisitionStatusList`,
			"GET"
		);

	getRequisitionList = async (
		model: TableFilterModel
	): Promise<IRequisitionGridDetail[]> =>
		this.callApi<IRequisitionGridDetail[]>(
			`Requisition/GetRequisitionList`,
			"POST",
			undefined,
			model
		);

	getRequisitionNumberList = async (
		model: ITableNumberColumnFilter
	): Promise<IRequisitionNumber[]> =>
		this.callApi<IRequisitionNumber[]>(
			`Requisition/GetRequisitionNumberList`,
			"POST",
			undefined,
			model
		);

	getRequisitionDetail = async (
		requisitionId: number,
		requisitionStepHistoryId: number,
		isPurchaseOrder: boolean = false
	): Promise<RequisitionDetailModel> =>
		this.callApi<RequisitionDetailModel>(
			`Requisition/GetRequisitionData/${requisitionId}/${isPurchaseOrder}`,
			"GET",
			requisitionStepHistoryId > 0
				? new URLSearchParams({
						requisitionStepHistoryId: requisitionStepHistoryId.toString(),
				  })
				: undefined
		);

	saveRequisitionDetail = async (
		model: RequisitionDetailModel
	): Promise<{ requisitionId: number; requisitionNumber: string }> =>
		this.callApi<{ requisitionId: number; requisitionNumber: string }>(
			`Requisition/Save`,
			"POST",
			undefined,
			model
		);

	updateRequisitionDetail = async (
		model: RequisitionDetailModel
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Requisition/Update/${model.requisitionId}`,
			"PUT",
			undefined,
			model
		);

	deleteRequisitionDetail = async (
		requisitionId: number,
		requisitionStepHistoryId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Requisition/Delete/${requisitionId}`,
			"DELETE",
			requisitionStepHistoryId > 0
				? new URLSearchParams({
						requisitionStepHistoryId: requisitionStepHistoryId.toString(),
				  })
				: undefined,
			undefined
		);

	getRequisitionStepHistoryList = async (
		requisitionId: number
	): Promise<IRequisitionStepHistory[]> =>
		this.callApi<IRequisitionStepHistory[]>(
			`RequisitionStepHistory/GetRequisitionStepHistoryList/${requisitionId}`,
			"GET"
		);

	submitRequisition = async (
		requisitionId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Requisition/SubmitRequisition`,
			"PUT",
			new URLSearchParams({
				requisitionId: requisitionId.toString(),
			}),
			{
				comment: comment,
			}
		);

	getReadyStatusRequisitionDetails = async (
		requisitionId: string,
		requisitionStepHistoryId: string,
		budgetChangeOrderNumber = ""
	): Promise<ReadyStatusRequisitionDetail> =>
		this.callApi<ReadyStatusRequisitionDetail>(
			`Requisition/GetReadyStatusRequisitionDetails`,
			"POST",
			undefined,
			{
				Key: requisitionId.toString(),
				Key_1: requisitionStepHistoryId.toString(),
				Key_2: budgetChangeOrderNumber.toString(),
			}
		);

	updateRequisitionStepCompleteAndApprove = async (
		requisitionId: number,
		requisitionStepHistoryId: number,
		comment: string
	): Promise<{ isThisLastStep: boolean }> =>
		this.callApi<{ isThisLastStep: boolean }>(
			`Requisition/UpdateRequisitionStepCompleteAndApprove/${requisitionId}/${requisitionStepHistoryId}`,
			"POST",
			undefined,
			{
				comment: comment,
			}
		);

	getStepsBeforeCurrentStep = async (
		requisitionId: number,
		requisitionStepHistoryId: number
	): Promise<IPreviousRequisitionStepHistory[]> =>
		this.callApi<IPreviousRequisitionStepHistory[]>(
			`RequisitionStepHistory/GetStepsBeforeCurrentStep/${requisitionId}/${requisitionStepHistoryId}`,
			"GET"
		);

	updateRequisitionStepStatusChangeRequest = async (
		requisitionId: number,
		selectedStepRequisitionStepHistoryId: number,
		currentStepRequisitionStepHistoryId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Requisition/UpdateRequisitionStepStatusChangeRequest/${requisitionId}`,
			"POST",
			undefined,
			{
				comment: comment,
				selectedStepRequisitionStepHistoryId:
					selectedStepRequisitionStepHistoryId.toString(),
				currentStepRequisitionStepHistoryId:
					currentStepRequisitionStepHistoryId.toString(),
			}
		);

	rejectRequisition = async (
		requisitionId: number,
		requisitionStepHistoryId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Requisition/RejectRequisition/${requisitionId}/${requisitionStepHistoryId}`,
			"POST",
			undefined,
			{
				comment: comment,
			}
		);

	delegateRequisition = async (
		requisitionId: number,
		requisitionStepHistoryId: number,
		userId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Requisition/DelegateRequisition/${requisitionId}/${requisitionStepHistoryId}`,
			"POST",
			undefined,
			{
				comment: comment,
				userId: userId.toString(),
			}
		);

	getDelegatedUserList = async (
		requisitionStepHistoryId: number
	): Promise<IDelegateToUser[]> =>
		this.callApi<IDelegateToUser[]>(
			`Requisition/GetDelegatedUserList/${requisitionStepHistoryId}`,
			"GET"
		);

	checkIsRequisitionStepHistoryReady = async (
		requisitionStepHistoryId: number
	): Promise<boolean> =>
		this.callApi<boolean>(
			`Requisition/CheckIsRequisitionStepHistoryReady/${requisitionStepHistoryId}`,
			"GET"
		);

	copyRequisition = async (
		requisitionId: number,
		isForceCopy: boolean,
		isRequestedFromContract: boolean
	): Promise<{ requisitionId: number; requisitionNumber: string }> =>
		this.callApi<{ requisitionId: number; requisitionNumber: string }>(
			`Requisition/Copy/${requisitionId}`,
			"POST",
			new URLSearchParams({
				isForceCopy: isForceCopy.toString(),
				isRequestedFromContract: isRequestedFromContract.toString(),
			})
		);

	reSubmitRequisition = async (
		requisitionId: number,
		currentStepRequisitionStepHistoryId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Requisition/ReSubmitRequisition/${requisitionId}/${currentStepRequisitionStepHistoryId}`,
			"POST",
			undefined,
			{
				comment: comment,
			}
		);

	updateRequisitionStepStatusRecall = async (
		requisitionId: number,
		completedStepRequisitionStepHistoryId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Requisition/UpdateRequisitionStepStatusRecall/${requisitionId}`,
			"POST",
			undefined,
			{
				completedStepRequisitionStepHistoryId:
					completedStepRequisitionStepHistoryId.toString(),
				comment: comment,
			}
		);

	getCompletedRequisitionStepDetail = async (
		requisitionId: number
	): Promise<ICompletedStepHistory> =>
		this.callApi<ICompletedStepHistory>(
			`RequisitionStepHistory/GetCompletedRequisitionStepDetail/${requisitionId}`,
			"GET"
		);

	getRequisitionsDetailForConversionToBid = async (
		requisitionIds: string
	): Promise<RequisitionConversionToBidDetail> =>
		this.callApi<RequisitionConversionToBidDetail>(
			`Requisition/GetRequisitionsDetailForConversionToBid/${requisitionIds}`,
			"GET"
		);

	getSystemTemplates = async (
		purchaseTypeId: number,
		purchaseCategoryId: number,
		contractTypeId: number,
		transactionTypeId: number
	): Promise<IRequisitionSystemTemplate> =>
		this.callApi<IRequisitionSystemTemplate>(
			`Requisition/GetSystemTemplates/${purchaseTypeId}/${purchaseCategoryId}`,
			"GET",
			new URLSearchParams({
				contractTypeId: contractTypeId.toString(),
				transactionTypeId: transactionTypeId.toString(),
			})
		);

	getRequisitionSystemTemplates = async (
		requisitionId: number
	): Promise<IRequisitionSystemTemplate> =>
		this.callApi<IRequisitionSystemTemplate>(
			`Requisition/GetRequisitionSystemTemplates/${requisitionId}`,
			"GET"
		);

	previewRequisitionSystemTemplate = async (
		requisitionId: number
	): Promise<string> =>
		this.callApi<string>(
			`RequisitionSystemTemplateSection/Preview/${requisitionId}`,
			"GET"
		);

	saveRequisitionSystemTemplateSection = async (
		model: AddUpdateRequisitionSystemTemplateSection
	): Promise<RequisitionSystemTemplateModel[]> =>
		this.callApi<RequisitionSystemTemplateModel[]>(
			`RequisitionSystemTemplateSection/Save`,
			"POST",
			undefined,
			model
		);

	updateRequisitionSystemTemplateSection = async (
		model: AddUpdateRequisitionSystemTemplateSection
	): Promise<undefined> =>
		this.callApi<undefined>(
			`RequisitionSystemTemplateSection/Update/${model.requisitionId}`,
			"PUT",
			undefined,
			model
		);

	deleteRequisitionBidDocument = async (
		requisitionId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`RequisitionSystemTemplateSection/DeleteAll/${requisitionId}`,
			"DELETE"
		);

	getRequisitionProcurementMethodSelectionList = async (
		requisitionId: number,
		requisitionStepHistoryId: number
	): Promise<ProcurementMethodSelectionItem[]> =>
		this.callApi<ProcurementMethodSelectionItem[]>(
			`Requisition/GetRequisitionProcurementMethodSelectionList/${requisitionId}`,
			"GET",
			requisitionStepHistoryId > 0
				? new URLSearchParams({
						requisitionStepHistoryId: requisitionStepHistoryId.toString(),
				  })
				: undefined
		);

	getSupplierList = async (erpDbCompanyId: number): Promise<ISupplierUser[]> =>
		this.callApi<ISupplierUser[]>(
			`Requisition/GetSuppliersOrPanelsList/${erpDbCompanyId}/${ContractType.Standard}`,
			"GET"
		);

	getPanelList = async (erpDbCompanyId: number): Promise<IRequisitionPanel[]> =>
		this.callApi<IRequisitionPanel[]>(
			`Requisition/GetSuppliersOrPanelsList/${erpDbCompanyId}/${ContractType.Panel}`,
			"GET"
		);

	getContractDetailBySupplierId = async (
		supplierId: number
	): Promise<IRequisitionContractDetail[]> =>
		this.callApi<IRequisitionContractDetail[]>(
			`Requisition/GetContractDetailBySupplierId/${supplierId}`,
			"GET"
		);

	getModulePhaseTransactionTypeListByModuleId = async (
		moduleId: number,
		transactionTypeIds: string
	): Promise<IModulePhaseTransactionType[]> =>
		this.callApi<IModulePhaseTransactionType[]>(
			`ModulePhaseTransactionType/GetByModuleId/${moduleId}`,
			"GET",
			new URLSearchParams({
				transactionTypeIds: transactionTypeIds.toString(),
			})
		);

	getPanelSuppliers = async (
		contractId: number
	): Promise<IPanelSupplierUser[]> =>
		this.callApi<IPanelSupplierUser[]>(
			`Contract/GetPanelSuppliers/${contractId}`,
			"GET"
		);

	createSupplierProspect = async (supplierId: number): Promise<boolean> =>
		this.callApi<boolean>(
			`PurchaseOrder/CreateSupplierProspect/${supplierId}`,
			"GET"
		);

	generatePurchaseOrderRequest = async (
		requisitionIds: string
	): Promise<{ purchaseOrderNumbers: string[] }> =>
		this.callApi<{ purchaseOrderNumbers: string[] }>(
			`PurchaseOrder/GeneratePurchaseOrderRequest`,
			"POST",
			new URLSearchParams({
				requisitionIds: requisitionIds.toString(),
			})
		);

	createERPPurchaseOrder = async (
		poNumber: string
	): Promise<{ purchaseOrderNumber: string }> =>
		this.callApi<{ purchaseOrderNumber: string }>(
			`PurchaseOrder/CreateErpPurchaseOrder/${poNumber}`,
			"POST"
		);

	previewPoTemplate = async (requisitionId: number): Promise<string> =>
		this.callApi<string>(`Requisition/POTemplate/${requisitionId}`, "GET");

	capexSupportItemList = async (requisitionId: number): Promise<IItem[]> =>
		this.callApi<IItem[]>(
			`Requisition/CapexSupportItemList/${requisitionId}`,
			"GET"
		);
}

export const RequisitionService = new RequisitionServiceInternal();
